/**
 * TanStack Query hook for playthrough terms acceptance.
 * 
 * Checks whether the user has accepted the current playthrough terms version
 * for a specific section (high_play or live). Each section has independent acceptance.
 * Used by PlaythroughGate to show the acceptance modal on High Play
 * and Live Experience pages (and for direct game links with custom playthrough).
 */

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { apiClient } from '@/lib/api-client';

interface PlaythroughTermsStatus {
  success: boolean;
  accepted: boolean;
  currentVersion: string;
  acceptedVersion: string | null;
}

interface AcceptResponse {
  success: boolean;
  acceptedVersion: string;
}

interface GameSectionInfo {
  success: boolean;
  section: string | null;
  hasCustomPlaythrough: boolean;
  redirectPath: string | null;
}

const FIVE_MINUTES = 5 * 60 * 1000;

/**
 * Check whether the user has accepted the current playthrough terms for a specific section.
 */
export function usePlaythroughTermsStatus(section: 'high_play' | 'live') {
  return useQuery({
    queryKey: ['playthrough-terms', 'status', section],
    queryFn: async (): Promise<PlaythroughTermsStatus> => {
      const response = await apiClient.get<PlaythroughTermsStatus>(`/game/playthrough-terms/status/${section}`);
      return response || { success: false, accepted: false, currentVersion: '1.0', acceptedVersion: null };
    },
    staleTime: FIVE_MINUTES,
    gcTime: FIVE_MINUTES * 2,
  });
}

/**
 * Mutation to accept the current playthrough terms for a specific section.
 * Invalidates the status query for that section after successful acceptance.
 */
export function useAcceptPlaythroughTerms(section: 'high_play' | 'live') {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: async (): Promise<AcceptResponse> => {
      const response = await apiClient.post<AcceptResponse>(`/game/playthrough-terms/accept/${section}`, {});
      return response || { success: false, acceptedVersion: '' };
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['playthrough-terms', 'status', section] });
    },
  });
}

/**
 * Get the section a game belongs to (for redirect logic on direct game links).
 */
export function useGameSectionInfo(gameId: string | null) {
  return useQuery({
    queryKey: ['game-section', gameId],
    queryFn: async (): Promise<GameSectionInfo> => {
      if (!gameId) return { success: true, section: null, hasCustomPlaythrough: false, redirectPath: null };
      const response = await apiClient.get<GameSectionInfo>(`/game/game-section/${encodeURIComponent(gameId)}`);
      return response || { success: true, section: null, hasCustomPlaythrough: false, redirectPath: null };
    },
    staleTime: FIVE_MINUTES,
    gcTime: FIVE_MINUTES * 2,
    enabled: !!gameId,
  });
}

export type { PlaythroughTermsStatus, GameSectionInfo };
